<template>
  <div class="annotation-gallery-wrapper">
    <GalleryGrid
      ref="galleryGrid"
      v-model:page="currentPage"
      v-model:totalPages="totalPagesCount"
      :imageList="imageList"
      :totalImageCount="totalImageCount"
      :imagesPerPage="imagesPerPage"
      :annotationSets="annotationSets"
      class="annotation-editor-grid"
    >
      <template #header-first-row-start>
        <div class="gallery-actions">
          <span>{{ selectedImages && selectedImages.length > 0 ? `Selected Images (${selectedImages.length} / ${totalImageCount})` :`Images (${totalImageCount})` }}</span>
        </div>
      </template>
      <template #header-second-row-end>
        <div class="gallery-actions">
          <v-pagination
            id="gallery-page-selector"
            v-model="currentPage"
            class="pagination"
            :length="numPages"
            :total-visible="7"
            density="compact"
          />
          <FilterButton
            button-class="button-light filter-button"
            :filter="datasetFilters"
            :active="showFilterPanel"
            @click="showFilterPanel = !showFilterPanel"
            @clear-filters="clearDatasetFilters"
          >
            <v-icon start>mdi-filter</v-icon>
            Filter
          </FilterButton>
          <div class="sort-by">
            <GalleryImageSort v-model:sortBy="sortBy" v-model:ascend="reverse" />
          </div>
        </div>
      </template>
      <template #grid-item>
        <template v-if="isOpen">
          <AnnotationEditorGalleryGridItem
            v-for="imageObj in imageList"
            :key="imageObj.id"
            :imageObj="imageObj"
            :annotations="imageObj.annotations"
            :annotationSets="annotationSets"
            :annotationDisplayType="colorAnnotationsBy"
            :filterAnnotationsBySets="getDisplayAnnotationSet(imageObj)"
            :filterAnnotationsByLabelIndexes="displayLabels"
            :labels="labels"
            :imageReviewStatus="imageObj.image_review_status"
            :reviewSettings="reviewSettings"
            :splitAnnotations="showSplitAnnotations"
            v-bind="gridItemDisplayProp"
            @grid-item-clicked="imageClicked(imageObj)"
            @delete-image-from-dataset="deleteImageFromDataset"
          />
        </template>
      </template>
      <template #empty-state>
        <GalleryEmptyState ref="galleryEmptyState" :datasetID="dataset?.id" />
      </template>
    </GalleryGrid>

    <!-- Right Filter Panel -->
    <div :class="['filter-panel', { 'filter-panel--open': showFilterPanel }]">
      <div class="filter-panel__header">
        <h3>Filters</h3>
        <v-btn
          icon
          size="small"
          @click="showFilterPanel = false"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>
      <div class="filter-panel__content">
        <DatasetFilter
          v-model="datasetFilters"
          :dataset="dataset"
          :annotationSets="annotationSets"
          :labels="labels"
          :types="types"
        />
      </div>
    </div>
  </div>
</template>

<script>
import DatasetFilter from '@/components/DatasetComponent/DatasetFilters/DatasetFilter.vue';
import DatastoreConnect from '@/assets/js/DatastoreFunctions/datastore-interface';
import GalleryGrid from '@/components/DatasetComponent/GalleryComponent/GalleryGrid.vue';
import AnnotationEditorGalleryGridItem from '@/components/DatasetComponent/GalleryComponent/AnnotationEditorGalleryGridItem.vue';
import GalleryEmptyState from '@/components/DatasetComponent/GalleryComponent/GalleryEmptyState.vue';
import FilterButton from '@/components/FilterButton.vue';
import useTasks from '@/composables/annotationTool/useTasks.js';
import GalleryImageSort from '@/components/DatasetComponent/GalleryComponent/GalleryImageSort.vue';

export default {
  name: 'AnnotationEditorGallery',
  components: {
    GalleryGrid,
    DatasetFilter,
    AnnotationEditorGalleryGridItem,
    GalleryEmptyState,
    FilterButton,
    GalleryImageSort,
  },
  props: {
    isOpen: {
      type: Boolean,
      default: false,
    },
    images: {
      type: Array,
      default: () => [],
    },
    dataset: {
      type: Object,
      default: null,
    },
    reviewSettings: {
      type: Object,
      default: null,
    },
  },
  emits: ['image-clicked'],
  setup() {
    const {
      internalImageObj,
      pendingTaskImages,
      switchToImageID,
      switchToDoneImage,
      imageFilters,
      auditorType,
      updateTaskImageFilters,
    } = useTasks();

    return {
      internalImageObj,
      pendingTaskImages,
      switchToImageID,
      switchToDoneImage,
      imageFilters,
      auditorType,
      updateTaskImageFilters,
    };
  },
  data() {
    return {
      dataConnect: new DatastoreConnect(this.$store.state.enterpriseServerUrl),
      imageList: null,
      imageListPromise: null,
      imagesPerPage: 40,
      currentPage: 1,
      totalPagesCount: 0,
      totalImageCount: 0,
      minImageNumber: 0,
      maxImageNumber: 0,
      datasetFilters: {},
      controller: new AbortController(),
      annotationDisplaySettings: {
        displayAnnotationSets: { annotation_sets: [] },
      },
      sortBy: 'id',
      reverse: false,
      filtersUpdated: false,
      showFilterPanel: false,
    };
  },
  computed: {
    selectedImages() {
      // Return empty array since selection logic not implemented
      return [];
    },
    datasets() {
      return this.$store.state.datasets.datasetList;
    },
    types() {
      if (this.dataset) {
        return this.dataset.annotation_types;
      }
      return [];
    },
    labels() {
      if (this.dataset) {
        return this.dataset.labels;
      }
      return [];
    },
    annotationSets() {
      if (this.dataset) {
        return this.dataset.annotation_sets;
      }
      return [];
    },
    getImagesParams() {
      return {
        ...this.getImagesListParams,
        ...this.getImagesFilterParams,
      };
    },
    getImagesListParams() {
      if (!this.dataset) {
        return null;
      }

      const limit = this.imagesPerPage;
      const offset = (this.currentPage - 1) * this.imagesPerPage;
      const sort_by = this.sortBy;
      const reverse = this.reverse;

      const params = {
        sort_by,
        reverse,
        offset,
        limit,
        get_tags: true,
      };

      return params;
    },
    getImagesFilterParams() {
      if (!this.dataset) {
        return null;
      }

      const params = {
        images_filter: { dataset_id: this.dataset.id },
        image_files_filter: {},
        annotations_filter: {},
        image_review_status_filter: {},
        review_task_id: this.reviewSettings?.reviewTask.id,
        get_annotations: true,
        image_tags_filter: {},
        get_sensors: true,
        sensors_filter: {},
      };

      // Images filter
      if (this.datasetFilters.groupsFilter && this.datasetFilters.groupsFilter.length > 0) {
        params.images_filter.group_ids = this.datasetFilters.groupsFilter;
      }

      // Annotations filter
      if (this.datasetFilters.confidenceRangeFilter) {
        params.annotations_filter.lower_score = this.datasetFilters.confidenceRangeFilter[0];
        params.annotations_filter.upper_score = this.datasetFilters.confidenceRangeFilter[1];
      }
      if (this.datasetFilters.annotationTypeFilter && this.datasetFilters.annotationTypeFilter.length > 0) {
        params.annotations_filter.types = this.datasetFilters.annotationTypeFilter;
      }
      if (this.datasetFilters.annotationSetFilter && this.datasetFilters.annotationSetFilter.length > 0) {
        params.annotations_filter.annotation_set_ids = this.datasetFilters.annotationSetFilter.map((set) => set.id);
      }
      if (this.datasetFilters.imagesWithAnnotationSetFilter && this.datasetFilters.imagesWithAnnotationSetFilter.length > 0) {
        params.annotations_filter.images_with_annotation_set_ids = this.datasetFilters.imagesWithAnnotationSetFilter.map((set) => set.id);
      }
      if (this.datasetFilters.annotationLabelFilter && this.datasetFilters.annotationLabelFilter.length > 0) {
        params.annotations_filter.images_with_annotation_label_indexes = this.datasetFilters.annotationLabelFilter;
      }
      if (this.datasetFilters.boundingBoxSizeFilter) {
        Object.entries(this.datasetFilters.boundingBoxSizeFilter).forEach(([key, value]) => {
          if (key !== 'type' && value) {
            params.annotations_filter[`${key}_${this.datasetFilters.boundingBoxSizeFilter.type}`] = value;
          }
        });
      }

      // Image files filter
      if (this.datasetFilters.imageNameFilter && this.datasetFilters.imageNameFilter.length > 0) {
        params.image_files_filter.image_name = this.datasetFilters.imageNameFilter;
      }
      if (this.datasetFilters.timeRangeFilter) {
        params.image_files_filter.lower_time = this.datasetFilters.timeRangeFilter[0];
        params.image_files_filter.upper_time = this.datasetFilters.timeRangeFilter[1];
      }
      if (this.datasetFilters.dateRangeFilter) {
        if (this.datasetFilters.dateRangeFilter[0]) {
          params.image_files_filter.lower_date = new Date(this.datasetFilters.dateRangeFilter[0]).toISOString();
        }
        if (this.datasetFilters.dateRangeFilter[1]) {
          params.image_files_filter.upper_date = new Date(this.datasetFilters.dateRangeFilter[1]).toISOString();
        }
      }
      if (this.datasetFilters.cameraSourceFilter && this.datasetFilters.cameraSourceFilter.length > 0) {
        params.image_files_filter.image_sources = this.datasetFilters.cameraSourceFilter;
      }

      if (Object.keys(params.annotations_filter).length === 0) {
        delete params.annotations_filter;
      }

      if (this.datasetFilters.imageTagFilter && this.datasetFilters.imageTagFilter.length > 0) {
        params.image_tags_filter.tag_names = this.datasetFilters.imageTagFilter.map((tag) => tag.name);
      }

      if (Object.keys(params.image_tags_filter).length === 0) {
        delete params.image_tags_filter;
      }

      // if (this.datasetFilters.displayAnnotationsFilter && this.datasetFilters.displayAnnotationsFilter.value.length > 0) {
      //   if (this.datasetFilters.displayAnnotationsFilter.displayType === 'byAnnotationSets') {
      //     params.annotations_filter.aggregate_annotation_set_ids = this.datasetFilters.displayAnnotationsFilter.value;
      //   }
      //   // if (this.datasetFilters.displayAnnotationsFilter.displayType === 'byAnnotationLabels') {}
      // }

      return params;
    },
    gridItemDisplayProp() {
      if (!this.datasetFilters?.displayAnnotationsFilter) {
        return {};
      }
      if (this.datasetFilters?.displayAnnotationsFilter?.displayType === 'byAnnotationSets') {
        return { filterAnnotationsBySets: this.datasetFilters.displayAnnotationsFilter.value };
      }
      if (this.datasetFilters?.displayAnnotationsFilter?.displayType === 'byAnnotationLabels') {
        return { filterAnnotationsByLabelIndexes: this.datasetFilters.displayAnnotationsFilter.value };
      }
      return {};
    },
    colorAnnotationsBy() {
      if (this.annotationDisplaySettings?.colorAnnotationsBy?.type) {
        return this.annotationDisplaySettings?.colorAnnotationsBy?.type;
      }
      return 'set';
    },
    displayAnnotationSets() {
      if (this.annotationDisplaySettings?.displayAnnotationSets) {
        return this.annotationDisplaySettings?.displayAnnotationSets.annotation_sets;
      }

      return [];
    },
    displayLabels() {
      if (this.annotationDisplaySettings?.displayLabels) {
        return this.annotationDisplaySettings?.displayLabels.labels;
      }
      return [];
    },
    shouldShowSplitAnnotationOption() {
      if (this.auditorType === 'annotation') {
        return false;
      }
      return true;
    },
    showSplitAnnotations() {
      if (this.auditorType === 'annotation') {
        return true;
      }
      return this.annotationDisplaySettings?.splitAnnotations;
    },
    numPages() {
      return Math.ceil(this.totalImageCount / this.imagesPerPage);
    },
  },
  watch: {
    getImagesParams: {
      deep: true,
      async handler(params) {
        if (!params || !this.isOpen) {
          return;
        }
        this.imageListPromise = new Promise((resolve, reject) => {
          this.getImages().then((imageList) => {
            this.imageList = imageList;
            resolve(imageList);
          });
        });
      },
    },
    getImagesFilterParams: {
      deep: true,
      handler() {
        this.filtersUpdated = true;
      },
    },
    isOpen() {
      if (this.isOpen) {
        this.handleGalleryOpened();
      } else {
        this.handleGalleryClosed();
      }
    },
    imageList: {
      deep: true,
      handler(newList) {
        this.minImageNumber = ((this.currentPage - 1) * this.imagesPerPage) + 1;
        this.maxImageNumber = ((this.currentPage - 1) * this.imagesPerPage) + newList.length;
      },
    },
    internalImageObj() {
      if (!this.imageFilters || !this.isOpen) {
        return;
      }
      this.imageListPromise = new Promise((resolve, reject) => {
        this.getImages().then((imageList) => {
          this.imageList = imageList;
          resolve(imageList);
        });
      });
    },
    pendingTaskImages() {
      if (!this.imageFilters || !this.isOpen) {
        return;
      }
      this.imageListPromise = new Promise((resolve, reject) => {
        this.getImages().then((imageList) => {
          this.imageList = imageList;
          resolve(imageList);
        });
      });
    },
  },
  mounted() {
    this.annotationDisplaySettings.displayAnnotationSets.annotation_sets.push(this.reviewSettings?.reviewTask.source_annotation_set_id);
  },
  methods: {
    clearDatasetFilters() {
      this.datasetFilters = {};
    },
    async getSelectionImageCount(params) {
      const resp = await this.dataConnect.getReviewTaskImageList({ only_count: true, get_tags: true, ...params })
        .catch((error) => {
          // TODO: handle error
          console.log(error);
        });
      if (!resp || resp.error || !resp.result) {
        // TODO: handle error
        return 0;
      }

      return resp.result;
    },
    async getImages() {
      this.controller.abort();
      this.controller = new AbortController();
      const currentController = this.controller;

      this.totalImageCount = await this.getSelectionImageCount(this.getImagesFilterParams, currentController.signal);
      if (this.totalImageCount === 0) {
        return [];
      }

      // Handle case when currentPage is greater than the new total number of pages
      if (Math.ceil(this.totalImageCount / this.imagesPerPage) < this.currentPage) {
        let newPage = Math.ceil(this.totalImageCount / this.imagesPerPage);
        if (newPage < 1) {
          newPage = 1;
        }
        this.currentPage = newPage;
        return this.imageList;
      }

      const imageListParams = {
        ...this.getImagesListParams,
        ...this.getImagesFilterParams,
      };
      const resp = await this.dataConnect.getReviewTaskImageList(imageListParams, currentController.signal)
        .catch((error) => {
          if (currentController.signal.aborted) {
            return this.imageList;
          }
          console.log(error);
          alert(error);
          return this.imageList;
        });
      if (!resp || resp.error || !resp.result) {
        if (resp && resp.error) {
          console.error(resp.error);
          alert(resp.error.message);
        }
        return this.imageList;
      }

      return resp.result;
    },
    async deleteImageFromDataset(imageObj) {
      const param = {
        image_ids: [imageObj.id],
      };
      const resp = await this.dataConnect.deleteImageFromDataset(param);
      if (!resp || resp.error || !resp.result) {
        if (resp && resp.error) {
          console.error(resp.error);
        }
        alert("Image failed to delete");
      } else {
        this.imageListPromise = new Promise((resolve, reject) => {
          this.getImages().then((imageList) => {
            this.imageList = imageList;
            resolve(imageList);
          });
        });
      }
    },
    async imageClicked(imageObj) {
      if (imageObj?.image_review_status?.status === 'Done') {
        this.switchToDoneImage(imageObj);
        this.$emit('image-clicked', imageObj);
      } else {
        await this.switchToImageID(imageObj.id);
        this.$emit('image-clicked', imageObj);
      }

      this.imageListPromise = new Promise((resolve, reject) => {
        this.getImages().then((imageList) => {
          this.imageList = imageList;
          resolve(imageList);
        });
      });
    },
    getDisplayAnnotationSet(imageObj) {
      if (imageObj?.image_review_status?.status === 'Done') {
        return [this.reviewSettings?.reviewTask.dest_annotation_set_id];
      } else if (this.annotationDisplaySettings?.displayAnnotationSets) {
        return this.annotationDisplaySettings?.displayAnnotationSets.annotation_sets;
      }
      return [];
    },
    handleGalleryOpened() {
      this.filtersUpdated = false;

      this.imageListPromise = new Promise((resolve, reject) => {
        this.getImages().then((imageList) => {
          this.imageList = imageList;
          resolve(imageList);
        });
      });
    },
    handleGalleryClosed() {
      if (this.filtersUpdated) {
        this.updateTaskImageFilters(this.getImagesFilterParams);
      }
      this.filtersUpdated = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.annotation-gallery-wrapper {
  display: flex;
  flex-direction: row;
  height: 100%;
  width: 100%;
  position: relative;
}

.annotation-editor-grid {
  flex: 1 1 auto;
  min-width: 0;
  overflow: auto;
}

.filter-panel {
  position: fixed;
  right: 0;
  top: 0;
  height: 100%;
  width: 0;
  background: white;
  border-left: 1px solid #e0e0e0;
  box-shadow: -2px 0 8px rgba(0, 0, 0, 0.1);
  transition: width 0.3s ease-in-out;
  overflow-y: auto;
  z-index: 1300;
  display: flex;
  flex-direction: column;

  &--open {
    width: 380px;
  }

  &__header {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    padding: 16px;
    border-bottom: 1px solid #e0e0e0;
    flex-shrink: 0;

    h3 {
      margin: 0;
      font-size: 1.125rem;
      font-weight: 600;
    }
  }

  &__content {
    flex: 1 1 auto;
    overflow-y: auto;
    padding: 16px;

    // Style the DatasetFilter component inside
    :deep(.dataset-filter) {
      display: flex;
      flex-direction: column;
      gap: 12px;
    }
  }
}

.filter-btn-label {
  margin-left: 4px;
  font-size: 0.875rem;
}

.controls-bar {
  display: none;
}

.grid-header-start {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  height: 100%;
  align-items: center;
  gap: 8px;
}

.grid-header-end {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  height: 100%;
  align-items: center;
  gap: 8px;
  justify-content: flex-end;
}

#dataset-select {
  width: 300px;
}

.gallery-image-count {
  margin-left: 16px;
  font-size: 0.75rem;
  font-weight: 600;
  @include themify() {
    color: themed('body-text-color-secondary');
  }
}

.sort-by {
  display: flex;
  flex-direction: row;
  align-items: center;

  &__select {
    width: 130px;
  }

  &__reverse._invert {
    transform: rotate(180deg);
  }
}

.filter-button {
  text-transform: capitalize;
  border: 1px solid #d0d0d0;

  :deep(.v-btn__content) {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 4px;
  }
}

.gallery-actions {
  display: flex;
  flex-direction: row;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  height: 100%;

  span{
    font-weight: bold;
    font-size: 1.15rem;
  }

  button {
    background: white;
    position: relative;
    display: flex;
    flex-direction: row;
    align-items: center;
    height: 26px;
    box-shadow: 1px 2px 4px 1px rgba(0, 0, 0, 0.2), 0 0 1px 1px rgba(0, 0, 0, 1);
    flex: 1 1 auto;
    padding: 4px 8px;
    gap: 2px;
    border-right: solid 1px rgba(0,0,0,0.5);
    max-width: fit-content;

    @include themify() {
      color: themed('body-text-color')
    }

    &.import {
      background: var(--button-color);
      color: var(--button-text-color);

      svg {
        margin-right: 5px;
      }
    }

    &:hover {
      background: rgba(var(--color-primary-100-rgb), 0.5);
    }

    &.delete:not([disabled]) {
      @include themify() {
        background: themed('button-delete');
        color: themed('button-delete-text');
      }
    }

    &.delete[disabled] {
      border: none;
    }

    &.delete:hover:not([disabled]) {
      @include themify() {
        background: $color-red-300;
        color: themed('button-delete-text');
      }
    }

    &.inactive {
      background: rgba(var(--color-disabled), 0.5) !important;
    }
  }
}
</style>
